import type { AxiosError, CancelTokenSource } from "axios";
import axios from "axios";
import type {
  ChargerData,
  FetchChargerListPayload,
  SessionData,
} from "../types/chargerTypes";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { CustomerSite } from "../types/customerTypes";
import { axiosInstance } from "../../utils/common";
import dayjs from "dayjs";

let cancelTokenSource2: CancelTokenSource | null = null;

export const fetchChargerList = createAsyncThunk<
  ChargerData[], // Type of the fulfilled action payload
  FetchChargerListPayload, // Type of the argument passed to the async thunk
  {
    rejectValue: string; // Type of the rejected action payload
  }
>("sites/fetchChargerList", async (payload, thunkAPI) => {
  const { selectedCustomerName, avail_sites, selectedSite } = payload;

  // Cancel the previous request, if any
  if (cancelTokenSource2) {
    cancelTokenSource2.cancel("Request canceled");
  }

  // Create a new cancel token source for the new request
  cancelTokenSource2 = axios.CancelToken.source();

  // Find the site id based on the selected site name
  const site = avail_sites.find((x) => x.name === selectedSite);
  if (!site) {
    return thunkAPI.rejectWithValue("Site not found");
  }

  try {
    // Make the API request
    const { data } = await axiosInstance.get<ChargerData[]>(
      `charger/list/${site.id}/`,
      {
        headers: {
          customer: selectedCustomerName, // Include the customer attribute in the headers
        },
        cancelToken: cancelTokenSource2.token,
      }
    );

    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

interface Payload {
  selectedCustomerName: string;
  avail_sites: CustomerSite[];
  selectedSite: string;
  fromDate: string;
  toDate: string;
}

let cancelTokenSource: CancelTokenSource | null = null;

export const fetchSummaryData = createAsyncThunk<SessionData[], Payload>(
  "trend/summary",
  async (payload, thunkAPI) => {
    const {
      selectedCustomerName,
      avail_sites,
      selectedSite,
      fromDate,
      toDate,
    } = payload;

    // Cancel the previous request, if any
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Request canceled");
    }

    // Create a new cancel token source for the new request
    cancelTokenSource = axios.CancelToken.source();

    // Function to find the site ID based on the selected site name
    const findSiteId = (siteName: string) =>
      avail_sites.find((site) => site.name === siteName);

    const siteId = findSiteId(selectedSite);

    if (!siteId) {
      return thunkAPI.rejectWithValue("Site not found");
    }

    try {
      // Make the API request with the cancel token
      const { data } = await axiosInstance.get<SessionData[]>(
        `charger/sessions/?from_date=${fromDate}&to_date=${toDate}&site_id=${siteId.id}`,
        {
          headers: { customer: selectedCustomerName },
          cancelToken: cancelTokenSource.token,
        }
      );
      return data;
    } catch (error: any) {
      // Check if the request was canceled
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue({ canceled: true });
      } else {
        return thunkAPI.rejectWithValue(error.message);
      }
    }
  }
);

interface FetchConnectorHistoryPayload {
  selectedCustomerName: string;
  avail_sites: CustomerSite[];
  selectedSite: string;
  fromDate: string;
  toDate: string;
}

let cancelTokenSourceConnectorHistory: CancelTokenSource | null = null;

export const fetchConnectorHistory = createAsyncThunk<any[], FetchConnectorHistoryPayload>(
  "charger/fetchConnectorHistory",
  async (payload, thunkAPI) => {
    const { selectedCustomerName, avail_sites, selectedSite, fromDate, toDate } = payload;

    if (cancelTokenSourceConnectorHistory) {
      cancelTokenSourceConnectorHistory.cancel("Request canceled");
    }
    cancelTokenSourceConnectorHistory = axios.CancelToken.source();

    const site = avail_sites.find((s) => s.name === selectedSite);
    if (!site) {
      return thunkAPI.rejectWithValue("Site not found");
    }

    try {
      const { data } = await axiosInstance.get<any[]>(
        `charger/connector_history/?from_date=${fromDate}&to_date=${toDate}&site_id=${site.id}`,
        {
          headers: { customer: selectedCustomerName },
          cancelToken: cancelTokenSourceConnectorHistory.token,
        }
      );
      return data;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue({ canceled: true });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

interface FetchSessionsPayload {
  fromDate?: string;
  toDate?: string;
  charger_id: number;
  selectedCustomerName: string;
}

export const fetchAllSessionDataForChargers = createAsyncThunk<
  SessionData[], // Return type (array of SessionData)
  FetchSessionsPayload, // Argument type
  { rejectValue: string } // Optional reject value
>(
  "sites/fetchAllSessionDataForChargers",
  async (payload: FetchSessionsPayload, thunkAPI) => {
    const { fromDate, toDate, charger_id, selectedCustomerName } =
      payload;

    // If there's already an ongoing request, cancel it
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Request canceled due to new request");
    }

    // Create a new cancel token for the current request
    cancelTokenSource = axios.CancelToken.source();

    const fromrange = fromDate ?? "";
    const torange = toDate ?? "";

    try {
      // API call
      const { data } = await axiosInstance.get<SessionData[]>(
        `charger/sessions/?from_date=${fromrange}&to_date=${torange}&charger_id=${charger_id}`,
        {
          headers: {
            customer: selectedCustomerName,
          },
          cancelToken: cancelTokenSource.token, // Use the new cancel token
        }
      );

      return data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue("Request canceled"); // Optional: you can return an empty array or specific error message
      }
      return thunkAPI.rejectWithValue((error as AxiosError).message);
    }
  }
);

// --- Site-level sessions for Chargers page ---
interface FetchSiteSessionPayload {
  fromDate?: string;
  toDate?: string;
  selectedCustomerName: string;
  site_id: number;
}

let cancelTokenSourceSiteSession: CancelTokenSource | null = null;

export const fetchSiteSessionData = createAsyncThunk<
  SessionData[],
  FetchSiteSessionPayload,
  { rejectValue: string }
>(
  "sites/fetchSiteSessionData",
  async (payload: FetchSiteSessionPayload, thunkAPI) => {
    const { fromDate, toDate, selectedCustomerName, site_id } = payload;

    if (cancelTokenSourceSiteSession) {
      cancelTokenSourceSiteSession.cancel("Request canceled due to new request");
    }

    cancelTokenSourceSiteSession = axios.CancelToken.source();

    const fromrange = fromDate ?? dayjs().subtract(2, "day").toISOString();
    const torange = toDate ?? dayjs().toISOString();

    try {
      const { data } = await axiosInstance.get<SessionData[]>(
        `charger/sessions/?from_date=${fromrange}&to_date=${torange}&site_id=${site_id}`,
        {
          headers: { customer: selectedCustomerName },
          cancelToken: cancelTokenSourceSiteSession.token,
        }
      );

      return Array.isArray(data) ? data : [];
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue("Request canceled");
      }
      return thunkAPI.rejectWithValue((error as AxiosError).message);
    }
  }
);

interface FetchChargerStatusPayload {
  charger_id: number;
  selectedCustomerName: string;
  fromDate: string;
  toDate: string;
}

let cancelTokenSource3: CancelTokenSource | null = null;

export const fetchChargerStatusForErrorTab = createAsyncThunk<
  SessionData[],
  FetchChargerStatusPayload
>("charger/fetchChargerStatusForErrorTab", async (payload, thunkAPI) => {
  try {
    const { charger_id, selectedCustomerName, fromDate, toDate } = payload;
    cancelTokenSource3 = axios.CancelToken.source();

    const { data } = await axiosInstance.get<SessionData[]>(
      `charger/sessions/?from_date=${fromDate}&to_date=${toDate}&charger_id=${charger_id}`,
      {
        headers: {
          customer: selectedCustomerName,
        },
        cancelToken: cancelTokenSource3?.token,
      }
    );

    return data;
  } catch (error: any) {
    if (axios.isCancel(error)) {
      return thunkAPI.rejectWithValue({ canceled: true });
    } else {
      return thunkAPI.rejectWithValue((error as AxiosError).message);
    }
  }
});

interface FetchConnectorHistoryForChargerPayload {
  charger_id: number;
  selectedCustomerName: string;
  fromDate: string;
  toDate: string;
}

let cancelTokenSourceConnectorHistoryForCharger: CancelTokenSource | null = null;

export const fetchConnectorHistoryForCharger = createAsyncThunk<
  any[],
  FetchConnectorHistoryForChargerPayload
>("charger/fetchConnectorHistoryForCharger", async (payload, thunkAPI) => {
  try {
    const { charger_id, selectedCustomerName, fromDate, toDate } = payload;

    if (cancelTokenSourceConnectorHistoryForCharger) {
      cancelTokenSourceConnectorHistoryForCharger.cancel("Request canceled");
    }
    cancelTokenSourceConnectorHistoryForCharger = axios.CancelToken.source();

    const { data } = await axiosInstance.get<any[]>(
      `charger/connector_history/?from_date=${fromDate}&to_date=${toDate}&charger_id=${charger_id}`,
      {
        headers: {
          customer: selectedCustomerName,
        },
        cancelToken: cancelTokenSourceConnectorHistoryForCharger.token,
      }
    );

    return data;
  } catch (error: any) {
    if (axios.isCancel(error)) {
      return thunkAPI.rejectWithValue({ canceled: true });
    } else {
      return thunkAPI.rejectWithValue((error as AxiosError).message);
    }
  }
});
